import { NetworkBasePopUp } from '../NetworkBasePopUp';
import { translateFn } from '../../../../utils/translator.helper';
import { NetworkPopUpType } from '../../network.pop-up.factory';
import { GzExchangeScanTaskWebComponent } from './gzExchangeScanTask';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import {
  DEFAULT_ARCHIVE_MAXIMUM_DEPTH,
  DEFAULT_ATTACHMENT_MAX_SIZE,
  ExchangeScanActions,
  ExchangeScanGeneralSettings,
  ExchangeScanOptions,
  ExchangeScanTargetSettings,
  ExchangeScanTaskSpecifics,
} from './interfaces';
import { CreateTaskRequest, TaskTarget } from '../../types';
import { TaskType } from '../../../../tasks/networkTasks/types';

export class ExchangeScanTaskPopUp extends NetworkBasePopUp<GzExchangeScanTaskWebComponent> {
  private type: NetworkPopUpType = NetworkPopUpType.EXCHANGE_SCAN_TASK;
  protected title = translateFn('Exchange scan');

  protected extraDialogConfigs: Partial<GzDialog> = {
    isWindow: true,
  };

  protected customSetup() {
    this.popUpContent = new GzExchangeScanTaskWebComponent();
  }

  protected async handleConfirmButtonClicked(event: CustomEvent<any>): Promise<void> {
    const formValue = this.popUpContent.getFormValue();
    this.popUpContent.unsubscribeForms();

    const exchangeScanTaskSpecifics: ExchangeScanTaskSpecifics = this.prepareExchangeScanTaskPayload(formValue);

    if (!this.data.targets) {
      throw new Error('Targets are not defined');
    }

    const request: CreateTaskRequest<ExchangeScanTaskSpecifics> = {
      settings: {
        specifics: exchangeScanTaskSpecifics,
        taskName: exchangeScanTaskSpecifics.general.name,
        targets: this.data.targets,
        type: TaskType.TASK_TYPE_SCAN_EXCHANGE_PARENT,
      },
    };

    await this.createTask<ExchangeScanTaskSpecifics>(request);
  }

  protected handleCancelButtonClicked(event: CustomEvent<any>): void {
    return;
  }

  private prepareExchangeScanTaskPayload(formValue: FormGroupValue): ExchangeScanTaskSpecifics {
    const taskGeneralSettings: ExchangeScanGeneralSettings = this.getExchangeTaskGeneralSettings(formValue);

    const taskTargetSettings: ExchangeScanTargetSettings = this.getExchangeTaskTargetSettings(formValue);

    const taskOptions: ExchangeScanOptions = this.getExchangeTaskScanOptions(formValue);

    const taskActions: ExchangeScanActions = this.getExchangeTaskScanActions(formValue);

    return {
      actions: taskActions,
      general: taskGeneralSettings,
      options: taskOptions,
      target: taskTargetSettings,
    };
  }

  private getExchangeTaskGeneralSettings(formValue: any): ExchangeScanGeneralSettings {
    const useStopScan: boolean = formValue.general.stopScanTask !== undefined;

    const generalSettings: ExchangeScanGeneralSettings = {
      name: formValue.general.name,
      useStopScan: useStopScan,
      interval: undefined,
      occurrence: undefined,
    };

    if (!useStopScan) {
      return generalSettings;
    }

    generalSettings.interval = formValue.general.stopScanTask.inputValue.toString();
    generalSettings.occurrence = formValue.general.stopScanTask.selectedTimeUnit;

    return generalSettings;
  }

  private getExchangeTaskTargetSettings(formValue: any): ExchangeScanTargetSettings {
    const taskTargetSettings: ExchangeScanTargetSettings = {
      exclusions: undefined,
      exclusionsEnabled: null,
      scanOnlyRecentReceivedStep: undefined,
      scanOnlyRecentReceivedStepType: undefined,
      scanOnlyRecentReceivedEnabled: false,
      scanAppointments: formValue.targetSettings.scanObjects.scanAppointments,
      scanContacts: formValue.targetSettings.scanObjects.scanContacts,
      scanEmails: formValue.targetSettings.scanObjects.scanEmail,
      scanPostItems: formValue.targetSettings.scanObjects.scanPostItems,
      scanTasks: formValue.targetSettings.scanObjects.scanTasks,
      scanOnlyItemsWithAttachments: formValue.targetSettings.scanRestrictions.scanItemsWithAttachments,
      scanOnlyUnreadEmails: formValue.targetSettings.scanRestrictions.scanUnreadEmails,
      targetType: formValue.targetSettings.scanTarget,
    };

    if (formValue.useExclusions) {
      taskTargetSettings.exclusionsEnabled = true;
      taskTargetSettings.exclusions = this.popUpContent.getExclusionsItems();
    }

    if (formValue.targetSettings.scanRestrictions.scanItemsReceivedLast !== undefined) {
      taskTargetSettings.scanOnlyRecentReceivedEnabled = true;
      taskTargetSettings.scanOnlyRecentReceivedStep =
        formValue.targetSettings.scanRestrictions.scanItemsReceivedLast.inputValue.toString();
      taskTargetSettings.scanOnlyRecentReceivedStepType =
        formValue.targetSettings.scanRestrictions.scanItemsReceivedLast.selectedTimeUnit;
    }

    return taskTargetSettings;
  }

  private getExchangeTaskScanOptions(formValue: any): ExchangeScanOptions {
    return {
      maximumDepthEnabled: !!formValue.scanOptions.archiveMaximumDepth,
      maximumDepth: formValue.scanOptions.archiveMaximumDepth
        ? formValue.scanOptions.archiveMaximumDepth.toString()
        : DEFAULT_ARCHIVE_MAXIMUM_DEPTH,
      maximumSizeEnabled: !!formValue.scanOptions.attachmentMaxSize,
      maximumSize: formValue.scanOptions.attachmentMaxSize
        ? formValue.scanOptions.attachmentMaxSize.toString()
        : DEFAULT_ATTACHMENT_MAX_SIZE,
      scanMode: formValue.scanOptions.scanFileTypes,
      scanPUA: formValue.scanOptions.scanForPUA,
      extensions: formValue.scanOptions.useExtensions ? formValue.scanOptions.useExtensions.extensions : undefined,
    };
  }

  private getExchangeTaskScanActions(formValue: any): ExchangeScanActions {
    return {
      infected: {
        action: formValue.scanActions.defaultActionInfectedFile,
        then: formValue.scanActions.alternativeActionInfectedFile,
      },
      suspected: {
        action: formValue.scanActions.defaultActionSuspectFile,
        then: formValue.scanActions.alternativeActionSuspectFile,
      },
      unscannable: {
        action: formValue.scanActions.defaultActionUnscannableFile,
        then: formValue.scanActions.alternativeActionUnscannableFile,
      },
    };
  }
}
